import {
  addItemQueryKeys,
  LEGACY_CLASS_IDS,
  pickResult,
  SEARCH_DEBOUNCE_MS,
  startManual,
  type AdvancedSearchFields,
  type LegacyCategory,
  type SearchPage,
  type SearchResult,
} from "@kovernow/flows/add_item";
import { toRecoveryAction } from "@kovernow/flows/recovery";
import { Button } from "@kovernow/react-ui/components/button";
import { useInfiniteQuery, useQuery, type InfiniteData, type UseInfiniteQueryResult } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Combobox, type ComboboxOption } from "../../components/Combobox";
import { FormField } from "../../components/FormField";
import { Recovery } from "../../components/Recovery";
import { useServices } from "../../core/app-context";
import { useT } from "../../core/use-t";
import { STEP_HEADING_ID, StepHeading, type StepProps } from "./shared";

type Results = UseInfiniteQueryResult<InfiniteData<SearchPage, number>, Error>;

const resultLabel = (r: SearchResult) => [r.brand, r.model].filter(Boolean).join(" ");
const BRAND_OPTION_LIMIT = 50;
const EMPTY_ADVANCED: AdvancedSearchFields = { brand: "", type: "", model: "" };

/**
 * Step 2: the item database. Simple search is an editable combobox over
 * `GET /items/search` (debounced 300 ms; the server filters). Advanced
 * search: Brand (a combobox over `listBrands`, free text if that fails),
 * Type and Model, run with "Search". Picking a result pre-fills and locks
 * brand/model/type; "Can't find it?" opens a blank details step.
 */
export function SearchStep({
  category,
  setFields,
  goTo,
  back,
  headingRef,
}: Pick<StepProps, "setFields" | "goTo" | "back" | "headingRef"> & { category: LegacyCategory }) {
  const t = useT();
  const { addItemApi } = useServices();
  const classId = LEGACY_CLASS_IDS[category];
  const [advanced, setAdvanced] = useState(false);

  const pick = (r: SearchResult) => {
    setFields((f) => pickResult(f, r));
    goTo("details");
  };
  const manual = () => {
    setFields((f) => startManual(f));
    goTo("details");
  };

  return (
    <section aria-labelledby={STEP_HEADING_ID} className="flex flex-col gap-4" data-testid="step-search">
      <StepHeading headingRef={headingRef}>
        {t("addItem.search.title", { category: t(`addItem.noun.${category}`) })}
      </StepHeading>
      <div>
        <Button
          type="button"
          variant="link"
          className="min-h-11 px-0"
          data-testid="toggle-advanced"
          onClick={() => setAdvanced((a) => !a)}
        >
          {advanced ? t("addItem.search.simple") : t("addItem.search.advanced")}
        </Button>
      </div>
      {advanced ? (
        <AdvancedSearch classId={classId} onPick={pick} />
      ) : (
        <SimpleSearch classId={classId} onPick={pick} search={addItemApi.search} />
      )}
      <div className="flex flex-col gap-3 border-t pt-4">
        <Button type="button" variant="outline" size="touch" data-testid="manual-entry" onClick={manual}>
          {t("addItem.search.manual")}
        </Button>
        <Button type="button" variant="ghost" size="touch" className="self-start" onClick={back}>
          {t("addItem.back")}
        </Button>
      </div>
    </section>
  );
}

function useSearchPages(
  key: readonly unknown[],
  enabled: boolean,
  fetchPage: (page: number) => Promise<SearchPage>,
): Results {
  return useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.hasNextPage ? last.page + 1 : undefined),
    enabled,
    retry: false,
  });
}

/** The loaded count (the API returns no total), "Show more" and failures. */
function ResultsFooter({ results, active }: { results: Results; active: boolean }) {
  const t = useT();
  const { format } = useServices();
  const loaded = results.data?.pages.reduce((n, p) => n + p.results.length, 0) ?? 0;
  return (
    <div className="flex flex-col gap-2">
      <p role="status" className="text-sm text-muted-foreground" data-testid="search-count">
        {active && results.data
          ? loaded > 0
            ? t("addItem.search.count", { count: loaded, n: format.number(loaded) })
            : t("addItem.search.none")
          : ""}
      </p>
      {active && results.isError ? (
        <div data-testid="search-error">
          <Recovery action={toRecoveryAction(results.error)} onRetry={() => void results.refetch()} retryable />
        </div>
      ) : null}
      {active && results.hasNextPage ? (
        <Button
          type="button"
          variant="outline"
          size="touch"
          className="self-start"
          data-testid="search-more"
          aria-disabled={results.isFetchingNextPage ? true : undefined}
          onClick={() => {
            if (!results.isFetchingNextPage) void results.fetchNextPage();
          }}
        >
          {results.isFetchingNextPage ? t("common.loading") : t("addItem.search.more")}
        </Button>
      ) : null}
    </div>
  );
}

function SimpleSearch({
  classId,
  onPick,
  search,
}: {
  classId: number;
  onPick: (r: SearchResult) => void;
  search: (classId: number, q: string, page: number) => Promise<SearchPage>;
}) {
  const t = useT();
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setQuery(input.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [input]);
  const active = query !== "";
  const results = useSearchPages(addItemQueryKeys.search(classId, query), active, (page) => search(classId, query, page));
  const all = useMemo(() => results.data?.pages.flatMap((p) => p.results) ?? [], [results.data]);
  const options: ComboboxOption[] = active
    ? all.map((r, i) => ({ id: `${r.universalId}-${i}`, label: resultLabel(r), description: r.type || undefined }))
    : [];
  return (
    <Combobox
      testId="item-search"
      label={t("addItem.search.label")}
      listLabel={t("addItem.search.results")}
      value={input}
      onValueChange={setInput}
      options={options}
      closeOnBlur={false}
      onSelect={(o) => {
        const r = all[options.indexOf(o)];
        if (r) onPick(r);
      }}
      footer={<ResultsFooter results={results} active={active} />}
    />
  );
}

function AdvancedSearch({ classId, onPick }: { classId: number; onPick: (r: SearchResult) => void }) {
  const t = useT();
  const { addItemApi } = useServices();
  const [fields, setFields] = useState<AdvancedSearchFields>(EMPTY_ADVANCED);
  const [submitted, setSubmitted] = useState<AdvancedSearchFields | null>(null);
  // Cached per class for the session.
  const brands = useQuery({
    queryKey: addItemQueryKeys.brands(classId),
    queryFn: () => addItemApi.listBrands(classId),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
  const active = submitted !== null;
  const results = useSearchPages(
    addItemQueryKeys.advanced(classId, submitted ?? EMPTY_ADVANCED),
    active,
    (page) => addItemApi.advanceSearch(classId, submitted ?? EMPTY_ADVANCED, page),
  );
  const all = results.data?.pages.flatMap((p) => p.results) ?? [];
  const needle = fields.brand.trim().toLocaleLowerCase();
  const brandOptions: ComboboxOption[] = (brands.data ?? [])
    .filter((b) => !needle || b.toLocaleLowerCase().includes(needle))
    .slice(0, BRAND_OPTION_LIMIT)
    .map((b) => ({ id: b, label: b }));
  const set = (k: keyof AdvancedSearchFields) => (v: string) => setFields((f) => ({ ...f, [k]: v }));

  return (
    <div className="flex flex-col gap-4" data-testid="advanced-search">
      {brands.isError ? (
        <FormField
          label={t("addItem.search.brand")}
          value={fields.brand}
          onChange={(e) => set("brand")(e.target.value)}
          data-testid="advanced-brand-text"
          autoComplete="off"
        />
      ) : (
        <Combobox
          testId="advanced-brand"
          label={t("addItem.search.brand")}
          listLabel={t("addItem.search.brandOptions")}
          value={fields.brand}
          onValueChange={set("brand")}
          options={brandOptions}
          onSelect={(o) => set("brand")(o.label)}
        />
      )}
      <FormField label={t("addItem.search.type")} value={fields.type} onChange={(e) => set("type")(e.target.value)} autoComplete="off" />
      <FormField label={t("addItem.search.model")} value={fields.model} onChange={(e) => set("model")(e.target.value)} autoComplete="off" />
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="touch" data-testid="advanced-run" onClick={() => setSubmitted({ ...fields })}>
          {t("addItem.search.run")}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="touch"
          onClick={() => {
            setFields(EMPTY_ADVANCED);
            setSubmitted(null);
          }}
        >
          {t("addItem.search.reset")}
        </Button>
      </div>
      {active && all.length ? (
        <ul aria-label={t("addItem.search.results")} className="flex flex-col divide-y rounded-lg border" data-testid="advanced-results">
          {all.map((r, i) => (
            <li key={`${r.universalId}-${i}`}>
              <button
                type="button"
                onClick={() => onPick(r)}
                className="flex min-h-11 w-full flex-col justify-center px-3 py-2 text-left hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
              >
                <span className="font-semibold">{resultLabel(r)}</span>
                {r.type ? <span className="text-sm text-muted-foreground">{r.type}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <ResultsFooter results={results} active={active} />
    </div>
  );
}
